import React from 'react';
import { Dropdown } from 'lib/components';

import { getAllCustomersForSuperAdmin } from 'lib/api';
import { uniqBy, debounce } from 'lodash';
import { SuperAdminCustomer } from 'lib/context';
import { VerificationStatus } from 'lib/const/VerificationStatus';
import { AllCustomerOption } from 'lib/const/SuperAdminOptions';

export type CustomerOption = {
  value: string | number;
  label: string | number;
  resellerId: number | null;
};

type Props = {
  width?: number;
  showAllCustomersOption?: boolean;
  customerFilter?: number;
  customerName?: string;
  placeholder?: string;
  zIndexProp?: number;
  onSelectCustomer(option: CustomerOption): void;
  status?: number;
  disabled?: boolean;
  setCustomersLength?: (value: number) => void;
};

export const CustomersDropdown = ({
  width,
  showAllCustomersOption = false,
  placeholder = 'All Customers',
  customerFilter,
  customerName,
  zIndexProp,
  onSelectCustomer,
  status = VerificationStatus.ALL,
  disabled = false,
  setCustomersLength,
}: Props) => {
  const [page, setPage] = React.useState(0);
  const [pageCopy, setPageCopy] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const [customersCount, setCustomersCount] = React.useState(0);
  const [customerSearch, setCustomerSearch] = React.useState('');
  const [dropdownOptions, setDropdownOptions] = React.useState<
    CustomerOption[]
  >(showAllCustomersOption ? [AllCustomerOption] : []);

  const searchInput = debounce((search: string) => {
    search ? setDropdownOptions([]) : setDropdownOptions(dropdownOptions);
    setCustomerSearch(search);
    search ? setPage(0) : setPage(pageCopy);
  }, 600);

  const fetchCustomers = async () => {
    try {
      const limit = 10;
      const start = page * limit;
      const params = {
        start,
        limit,
        search: customerSearch,
        status,
      };
      const { customers, count } = await getAllCustomersForSuperAdmin(
        params
      ).catch(() => {
        return { customers: [], count: 0 };
      });

      let customerOptions: CustomerOption[] = [...dropdownOptions];
      customers.forEach((customer: SuperAdminCustomer) => {
        customerOptions = [
          ...customerOptions,
          {
            label:
              customer.business && customer.business.trim()
                ? `${customer.business} - ${customer.customerId}`
                : `No Name - ${customer.customerId}`,
            value: customer.customerId,
            resellerId: customer?.resellerId || null,
          },
        ];
      });

      const selectedCustomer: CustomerOption | undefined = customerOptions.find(
        option => option.value === customerFilter
      );
      if (!selectedCustomer && !!customerFilter && !!customerName) {
        customerOptions.push({
          value: customerFilter,
          label: `${customerName} - ${customerFilter}`,
          resellerId: null,
        });
      }

      customerOptions = uniqBy(customerOptions, 'value');
      setDropdownOptions(customerOptions);
      setCustomersCount(count);
      setCustomersLength && setCustomersLength(count);
    } catch (error) {}
  };

  React.useEffect(() => {
    fetchCustomers();
  }, [page, customerSearch, customerName]);

  return (
    <Dropdown
      width={width}
      menuPortalTarget={document.body}
      menuPlacement={'bottom'}
      placeholder={placeholder}
      creatable={false}
      dropdownHeight={300}
      options={dropdownOptions}
      value={dropdownOptions.find(option => option.value === customerFilter)}
      onChange={option => onSelectCustomer(option)}
      onMenuScrollToBottom={() => {
        if (dropdownOptions.length < customersCount) setPage(page + 1);
        if (!customerSearch) setPageCopy(page + 1);
      }}
      onInputChange={searchInput}
      isLoading={loading}
      onMenuClose={() => setLoading(false)}
      className='option-container'
      isSearchable={true}
      zIndexProp={zIndexProp || 999}
      extendStyles={{
        valueContainer: {
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          flexWrap: 'nowrap',
        },
      }}
      filterOption={() => {
        return true;
      }}
      disabled={disabled}
    />
  );
};
